import { Box, Typography, useTheme } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { type ChildrenChartData, getChildrenChartAnnotations } from "Charts/Utils/ChildrenChartUtils";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getYearsInflected } from "Infrastructure/Utils/LanguageUtils";
import { Chart as ChartJS } from "chart.js/auto";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { addMonths, addYears, format, monthsToYears } from "date-fns";
import type React from "react";
import { Line } from "react-chartjs-2";

ChartJS.register(AnnotationPlugin);

type Props = {
  readonly referenceStartDate: Date;
  readonly data: ChildrenChartData;
  readonly earlyAgeWithdrawalIn?: number;
  readonly primarySchoolWithdrawalIn?: number;
  readonly highSchoolWithdrawalIn?: number;
  readonly universityWithdrawalIn?: number;
  readonly startToLifeWithdrawalIn?: number;
};

const getLabels = (lengthInMonths: number, referenceStartDate: Date) => {
  const padding = Math.floor(lengthInMonths * 0.05);

  return new Array(lengthInMonths + padding).fill("").map((_, index) => {
    return format(addMonths(referenceStartDate, index), "yyyy-MM-dd");
  });
};

export const ChildrenGoalChart: React.FC<Props> = (props) => {
  const theme = useTheme();
  const { t } = useResource();

  const { data } = props;

  const options = {
    locale: "en-US",
    barPercentage: 0.8,
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: {
      topLeft: 6,
      topRight: 6,
      bottomLeft: 6,
      bottomRight: 6,
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      customTicks: true,
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem: any) {
            return bodyItem.lines;
          }

          if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);
            let innerHtml = "<thead>";

            const years = monthsToYears(data.totalLengthInMonths);

            innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
              ${t(Resources.AnalysisDetail.Goals.InvestmentDuration)}: ${years} ${t(getYearsInflected(years))}
              </td></tr>`;

            const date = format(new Date(tooltipModel.dataPoints[0].label), "M/yyyy");
            const amount = `<span>${formatCurrency(Number(bodyLines[0][0].toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`;

            innerHtml += `<tr><td>${date}</td></tr><tr><td>${amount}</td></tr>`;
            innerHtml += "</tbody>";

            const tableRoot = tooltipEl.querySelector("table");

            if (tableRoot) {
              tableRoot.innerHTML = innerHtml;
            }
          }

          const chartPosition = context.chart.canvas.getBoundingClientRect();
          const tooltipPosition = tooltipEl.getBoundingClientRect();

          const width = tooltipPosition.right - tooltipPosition.left;
          const height = tooltipPosition.bottom - tooltipPosition.top;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left = `${chartPosition.left + window.pageXOffset + tooltipModel.caretX - width}px`;
          tooltipEl.style.top = `${chartPosition.top + window.pageYOffset + tooltipModel.caretY - height}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.zIndex = "10000";

          tooltipEl.style.fontFamily = "Roboto";
          tooltipEl.style.fontSize = "10px";
          tooltipEl.style.lineHeight = "1.1";
          tooltipEl.style.borderWidth = "1px";
          tooltipEl.style.borderStyle = "solid";
          tooltipEl.style.borderColor = colors.borderLight;
          tooltipEl.style.backgroundColor = colors.white;
          tooltipEl.style.borderRadius = theme.spacing(Radius.M);
          tooltipEl.style.padding = theme.spacing(Spacing.M);
        },
      },
      annotation: {
        annotations: {
          ...getChildrenChartAnnotations(
            data,
            props.earlyAgeWithdrawalIn,
            props.primarySchoolWithdrawalIn,
            props.highSchoolWithdrawalIn,
            props.universityWithdrawalIn,
            props.startToLifeWithdrawalIn,
          ),
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          autoSkip: false,
          color: "transparent",
          font: {
            weight: 400,
            size: 10,
            family: "Roboto",
            lineHeight: 1.3,
          },
          callback: (_: string | number, index: number) => {
            const date = props.referenceStartDate;

            if (index === 0) {
              return [date.getFullYear().toString(), "dnes"];
            }

            if (props.earlyAgeWithdrawalIn && props.earlyAgeWithdrawalIn * 12 === index) {
              return [addYears(date, props.earlyAgeWithdrawalIn).getFullYear().toString(), t(Resources.AnalysisDetail.Goals.EarlyAge)];
            }

            if (props.primarySchoolWithdrawalIn && props.primarySchoolWithdrawalIn * 12 === index) {
              return [
                addYears(date, props.primarySchoolWithdrawalIn).getFullYear().toString(),
                t(Resources.AnalysisDetail.Goals.PrimarySchool),
              ];
            }

            if (props.highSchoolWithdrawalIn && props.highSchoolWithdrawalIn * 12 === index) {
              return [addYears(date, props.highSchoolWithdrawalIn).getFullYear().toString(), t(Resources.AnalysisDetail.Goals.HighSchool)];
            }

            if (props.universityWithdrawalIn && props.universityWithdrawalIn * 12 === index) {
              return [addYears(date, props.universityWithdrawalIn).getFullYear().toString(), t(Resources.AnalysisDetail.Goals.University)];
            }

            if (props.startToLifeWithdrawalIn && props.startToLifeWithdrawalIn * 12 === index) {
              return [
                addYears(date, props.startToLifeWithdrawalIn).getFullYear().toString(),
                t(Resources.AnalysisDetail.Goals.StartToLife),
              ];
            }
          },
        },
      },
      y: {
        beginAtZero: true,
        stacked: false,
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        grace: "1",
      },
    },
  };

  return (
    <Box width={"100%"}>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.Common.Value)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Box position="relative" width={"100%"} minHeight={210}>
          <Line
            options={options}
            data={{
              labels: getLabels(data.dataset.length, props.referenceStartDate),
              datasets: [
                {
                  data: data.dataset,
                  backgroundColor: colors.chartPositive,
                  fill: true,
                  borderColor: "transparent",
                  pointStyle: "dash",
                },
              ],
            }}
            plugins={[customTicksPlugin]}
          />
        </Box>
      </Box>
    </Box>
  );
};

const customTicksPlugin = {
  id: "customTicks",
  afterDraw: (chart: any) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;

    const ticks = chart.scales.x.ticks;

    ticks.forEach((tick: any, index: any) => {
      const x = xAxis.getPixelForTick(index);
      const y = xAxis.bottom;

      // First line (e.g., month)
      ctx.font = "bold 10px Roboto";
      ctx.fillStyle = colors.textPrimary;
      ctx.textAlign = "center";
      ctx.fillText(tick.label[0], x, y - 16);

      ctx.font = "10px Roboto";
      ctx.fillStyle = colors.textSecondary;
      ctx.fillText(tick.label[1], x, y - 4);
    });

    ctx.save();
    ctx.restore();
  },
};
